import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ApplicantStatus } from "../../enums/ApplicantStatus.ENUM";
import { pick } from "./copilotUtils";

const toOptions = (rows) =>
  (rows || []).map((o) => ({
    id: Number(pick(o, "id", "Id")) || 0,
    name: pick(o, "name", "Name") || `#${pick(o, "id", "Id")}`,
    extra: pick(o, "extra", "Extra") || "",
  }));

const readForm = (block) =>
  pick(block, "interactionForm", "InteractionForm") || {};

const CopilotInteractionForm = ({
  block,
  workflow,
  disabled = false,
  onSave,
  onCancel,
  onRefresh,
}) => {
  const form = readForm(block);
  const statuses = useMemo(() => toOptions(pick(form, "statuses", "Statuses")), [form]);
  const rejectedReasons = useMemo(
    () => toOptions(pick(form, "rejectedReasons", "RejectedReasons")),
    [form]
  );
  const followUpReasons = useMemo(
    () => toOptions(pick(form, "followUpReasons", "FollowUpReasons")),
    [form]
  );
  const interviewRounds = useMemo(
    () => toOptions(pick(form, "interviewRounds", "InterviewRounds")),
    [form]
  );
  const interviewers = useMemo(
    () => toOptions(pick(form, "interviewers", "Interviewers")),
    [form]
  );
  const slots = useMemo(() => toOptions(pick(form, "slots", "Slots")), [form]);

  const answers = workflow?.answers || workflow?.Answers || {};
  const defaultComment =
    pick(form, "defaultComment", "DefaultComment") ||
    answers.comments ||
    answers.Comments ||
    "";

  const initialStatusId =
    Number(pick(form, "selectedStatusId", "SelectedStatusId")) ||
    Number(workflow?.statusId || workflow?.StatusId) ||
    null;

  const [statusId, setStatusId] = useState(initialStatusId);
  const [rejectedReasonId, setRejectedReasonId] = useState(
    Number(answers.rejectedReasonId || answers.RejectedReasonId) || null
  );
  const [followupReasonId, setFollowupReasonId] = useState(
    Number(answers.followupReasonId || answers.FollowupReasonId) || null
  );
  const [followUpDate, setFollowUpDate] = useState(() => {
    const raw = answers.followUpDateTime || answers.FollowUpDateTime;
    const d = raw ? new Date(raw) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  });
  const [interviewRoundId, setInterviewRoundId] = useState(
    Number(answers.interviewRoundId || answers.InterviewRoundId) || null
  );
  const [interviewDate, setInterviewDate] = useState(() => {
    const raw = answers.interviewDate || answers.InterviewDate;
    const d = raw ? new Date(raw) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  });
  const [interviewerId, setInterviewerId] = useState(
    Number(answers.interviewerId || answers.InterviewerId || workflow?.interviewerId) || null
  );
  const [slotId, setSlotId] = useState(Number(answers.slotId || answers.SlotId) || null);
  const [manualTime, setManualTime] = useState("");
  const [comments, setComments] = useState(answers.comments || answers.Comments || defaultComment);
  const [error, setError] = useState("");

  useEffect(() => {
    setStatusId(initialStatusId);
  }, [initialStatusId]);

  useEffect(() => {
    setComments((prev) => prev || defaultComment);
  }, [defaultComment]);

  const statusName = statuses.find((s) => s.id === statusId)?.name || "";
  const needsReject = statusId === ApplicantStatus.REJECTED;
  const needsFollowUp = statusId === ApplicantStatus.FOLLOW_UP;
  const needsSchedule =
    statusId === ApplicantStatus.SCHEDULE ||
    statusId === ApplicantStatus.READY_FOR_NEXT_ROUND;

  const buildWorkflowPatch = () => {
    const nextAnswers = {
      ...(answers || {}),
      statusId: statusId ? String(statusId) : "",
      statusName: statusName || "",
      comments: (comments || "").trim() || defaultComment,
    };

    if (needsReject) {
      const reason = rejectedReasons.find((r) => r.id === rejectedReasonId);
      nextAnswers.rejectedReasonId = rejectedReasonId ? String(rejectedReasonId) : "";
      nextAnswers.rejectedReasonName = reason?.name || "";
    } else {
      nextAnswers.rejectedReasonId = "";
      nextAnswers.rejectedReasonName = "";
    }

    if (needsFollowUp) {
      const reason = followUpReasons.find((r) => r.id === followupReasonId);
      nextAnswers.followupReasonId = followupReasonId ? String(followupReasonId) : "";
      nextAnswers.followupReasonName = reason?.name || "";
      nextAnswers.followUpDateTime = followUpDate ? followUpDate.toISOString() : "";
    } else {
      nextAnswers.followupReasonId = "";
      nextAnswers.followupReasonName = "";
      nextAnswers.followUpDateTime = "";
    }

    if (needsSchedule) {
      const round = interviewRounds.find((r) => r.id === interviewRoundId);
      const interviewer = interviewers.find((u) => u.id === interviewerId);
      const slot = slots.find((s) => s.id === slotId);
      nextAnswers.interviewRoundId = interviewRoundId ? String(interviewRoundId) : "";
      nextAnswers.interviewRoundName = round?.name || "";
      nextAnswers.interviewDate = interviewDate
        ? interviewDate.toISOString().slice(0, 10)
        : "";
      nextAnswers.interviewerId = interviewerId ? String(interviewerId) : "";
      nextAnswers.interviewerName = interviewer?.name || "";
      if (slotId && slot) {
        nextAnswers.slotId = String(slotId);
        nextAnswers.slotLabel = slot.name || "";
        if (slot.extra) {
          const parsed = new Date(slot.extra.replace(" ", "T"));
          if (!Number.isNaN(parsed.getTime())) {
            nextAnswers.scheduleDatetime = parsed.toISOString();
          }
        }
      } else if (manualTime.trim() && interviewDate) {
        nextAnswers.slotId = "";
        const [hh, mm] = manualTime.trim().split(":").map((n) => Number(n));
        const when = new Date(interviewDate);
        when.setHours(hh || 10, mm || 0, 0, 0);
        nextAnswers.scheduleDatetime = when.toISOString();
        nextAnswers.slotLabel = `${when.toLocaleString()} (manual)`;
      } else {
        nextAnswers.slotId = "";
        nextAnswers.slotLabel = "";
        nextAnswers.scheduleDatetime = "";
      }
    } else {
      nextAnswers.interviewRoundId = "";
      nextAnswers.interviewRoundName = "";
      nextAnswers.interviewDate = "";
      nextAnswers.interviewerId = "";
      nextAnswers.interviewerName = "";
      nextAnswers.slotId = "";
      nextAnswers.slotLabel = "";
      nextAnswers.scheduleDatetime = "";
    }

    return {
      ...(workflow || {}),
      type: workflow?.type || workflow?.Type || "update_candidate_status",
      step: "collect_form",
      statusId: statusId || null,
      statusName: statusName || null,
      interviewerId: needsSchedule ? interviewerId || null : null,
      interviewerName: needsSchedule
        ? interviewers.find((u) => u.id === interviewerId)?.name || null
        : null,
      awaitingConfirm: false,
      answers: nextAnswers,
      Answers: nextAnswers,
    };
  };

  const validate = () => {
    if (!statusId) return "Pick a status.";
    if (needsReject && !rejectedReasonId) return "Pick a rejected reason.";
    if (needsFollowUp && !followupReasonId) return "Pick a follow-up reason.";
    if (needsFollowUp && !followUpDate) return "Pick a follow-up date.";
    if (needsSchedule && !interviewRoundId) return "Pick an interview round.";
    if (needsSchedule && !interviewDate) return "Pick an interview date.";
    if (needsSchedule && !interviewerId) return "Pick an interviewer.";
    if (needsSchedule && !slotId && !manualTime.trim()) {
      return "Pick a slot or enter a manual time (e.g. 14:30).";
    }
    if (!(comments || "").trim() && !defaultComment) return "Add a short comment.";
    return "";
  };

  const handleSave = () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    setError("");
    onSave?.(buildWorkflowPatch());
  };

  const handleRefresh = (patch) => {
    const next = { ...buildWorkflowPatch(), ...patch };
    if (patch?.answers) {
      next.answers = { ...buildWorkflowPatch().answers, ...patch.answers };
      next.Answers = next.answers;
    }
    onRefresh?.(next);
  };

  const applicantName =
    pick(block, "applicantName", "ApplicantName") ||
    workflow?.applicantName ||
    workflow?.ApplicantName ||
    "Candidate";
  const applicantId =
    pick(block, "applicantId", "ApplicantId") ||
    workflow?.applicantId ||
    workflow?.ApplicantId;

  return (
    <div className="copilot-interaction-form">
      <div className="copilot-interaction-form__head">
        <i className="pi pi-pencil" />
        <div>
          <div className="copilot-interaction-form__title">Add interaction</div>
          <div className="copilot-interaction-form__sub">
            {applicantName}
            {applicantId ? ` (#${applicantId})` : ""}
          </div>
        </div>
      </div>

      <div className="copilot-interaction-form__field">
        <label htmlFor="copilot-status">Status</label>
        <Dropdown
          inputId="copilot-status"
          value={statusId}
          options={statuses}
          optionLabel="name"
          optionValue="id"
          placeholder="Select status"
          className="w-full"
          disabled={disabled}
          onChange={(e) => {
            setStatusId(e.value);
            setSlotId(null);
            setManualTime("");
            if (
              e.value === ApplicantStatus.SCHEDULE ||
              e.value === ApplicantStatus.READY_FOR_NEXT_ROUND
            ) {
              handleRefresh({
                statusId: e.value,
                statusName: statuses.find((s) => s.id === e.value)?.name || "",
                answers: {
                  statusId: String(e.value),
                  statusName: statuses.find((s) => s.id === e.value)?.name || "",
                },
              });
            }
          }}
        />
      </div>

      {needsReject ? (
        <div className="copilot-interaction-form__field">
          <label htmlFor="copilot-reject">Rejected reason</label>
          <Dropdown
            inputId="copilot-reject"
            value={rejectedReasonId}
            options={rejectedReasons}
            optionLabel="name"
            optionValue="id"
            placeholder="Select reason"
            className="w-full"
            disabled={disabled}
            onChange={(e) => setRejectedReasonId(e.value)}
          />
        </div>
      ) : null}

      {needsFollowUp ? (
        <>
          <div className="copilot-interaction-form__field">
            <label htmlFor="copilot-follow-reason">Follow-up reason</label>
            <Dropdown
              inputId="copilot-follow-reason"
              value={followupReasonId}
              options={followUpReasons}
              optionLabel="name"
              optionValue="id"
              placeholder="Select reason"
              className="w-full"
              disabled={disabled}
              onChange={(e) => setFollowupReasonId(e.value)}
            />
          </div>
          <div className="copilot-interaction-form__field">
            <label htmlFor="copilot-follow-date">Follow-up date</label>
            <Calendar
              inputId="copilot-follow-date"
              value={followUpDate}
              showTime
              hourFormat="12"
              className="w-full"
              disabled={disabled}
              onChange={(e) => setFollowUpDate(e.value)}
            />
          </div>
        </>
      ) : null}

      {needsSchedule ? (
        <>
          <div className="copilot-interaction-form__field">
            <label htmlFor="copilot-round">Interview round</label>
            <Dropdown
              inputId="copilot-round"
              value={interviewRoundId}
              options={interviewRounds}
              optionLabel="name"
              optionValue="id"
              placeholder="Select round"
              className="w-full"
              disabled={disabled}
              onChange={(e) => setInterviewRoundId(e.value)}
            />
          </div>
          <div className="copilot-interaction-form__field">
            <label htmlFor="copilot-interview-date">Interview date</label>
            <Calendar
              inputId="copilot-interview-date"
              value={interviewDate}
              dateFormat="yy-mm-dd"
              className="w-full"
              disabled={disabled}
              onChange={(e) => {
                setInterviewDate(e.value);
                setSlotId(null);
                if (e.value) {
                  handleRefresh({
                    answers: {
                      interviewDate: e.value.toISOString().slice(0, 10),
                      interviewerId: interviewerId ? String(interviewerId) : "",
                      statusId: statusId ? String(statusId) : "",
                      statusName,
                    },
                    statusId,
                    statusName,
                    interviewerId,
                  });
                }
              }}
            />
          </div>
          <div className="copilot-interaction-form__field">
            <label htmlFor="copilot-interviewer">Interviewer</label>
            <Dropdown
              inputId="copilot-interviewer"
              value={interviewerId}
              options={interviewers}
              optionLabel="name"
              optionValue="id"
              placeholder={interviewers.length ? "Select interviewer" : "No interviewers with slots"}
              className="w-full"
              disabled={disabled}
              onChange={(e) => {
                setInterviewerId(e.value);
                setSlotId(null);
                handleRefresh({
                  interviewerId: e.value,
                  answers: {
                    interviewerId: e.value ? String(e.value) : "",
                    interviewerName:
                      interviewers.find((u) => u.id === e.value)?.name || "",
                    interviewDate: interviewDate
                      ? interviewDate.toISOString().slice(0, 10)
                      : "",
                    statusId: statusId ? String(statusId) : "",
                    statusName,
                  },
                  statusId,
                  statusName,
                });
              }}
            />
          </div>
          <div className="copilot-interaction-form__field">
            <label htmlFor="copilot-slot">Available slot</label>
            <Dropdown
              inputId="copilot-slot"
              value={slotId}
              options={slots}
              optionLabel="name"
              optionValue="id"
              placeholder={slots.length ? "Select slot" : "No slots — use manual time"}
              className="w-full"
              disabled={disabled}
              onChange={(e) => {
                setSlotId(e.value);
                setManualTime("");
              }}
            />
          </div>
          <div className="copilot-interaction-form__field">
            <label htmlFor="copilot-manual-time">Or manual time (HH:mm)</label>
            <InputText
              id="copilot-manual-time"
              value={manualTime}
              placeholder="14:30"
              className="w-full"
              disabled={disabled}
              onChange={(e) => {
                setManualTime(e.target.value);
                if (e.target.value) setSlotId(null);
              }}
            />
          </div>
        </>
      ) : null}

      <div className="copilot-interaction-form__field">
        <label htmlFor="copilot-comments">Comments</label>
        <InputTextarea
          id="copilot-comments"
          value={comments}
          rows={3}
          className="w-full"
          disabled={disabled}
          onChange={(e) => setComments(e.target.value)}
        />
        <button
          type="button"
          className="copilot-interaction-form__link"
          disabled={disabled}
          onClick={() => setComments(defaultComment)}
        >
          Use default comment
        </button>
      </div>

      {error ? <div className="copilot-interaction-form__error">{error}</div> : null}

      <div className="copilot-interaction-form__actions">
        <Button
          type="button"
          label="Cancel"
          className="p-button-text p-button-sm"
          disabled={disabled}
          onClick={() => onCancel?.()}
        />
        <Button
          type="button"
          label="Save"
          icon="pi pi-check"
          className="p-button-sm"
          disabled={disabled}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

CopilotInteractionForm.propTypes = {
  block: PropTypes.object.isRequired,
  workflow: PropTypes.object,
  disabled: PropTypes.bool,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onRefresh: PropTypes.func,
};

export default CopilotInteractionForm;
